import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const MINE_SIZE = 8;
export const MINE_COUNT = 10;
/** Stepping on a mine costs this many points. */
export const MINE_PENALTY = 3;
/**
 * Safe reveals in a row before the turn passes anyway. Without a cap, a player
 * who deduces well never hits a mine and never gives the board back — the
 * other player could sit out the entire game.
 */
export const MINE_STREAK_LIMIT = 3;

export interface MinesweeperState extends BaseState {
	kind: "minesweeper";
	/** Emptied before being sent, except for mines already stepped on. */
	mines: number[];
	/** How many mines touch each cell. */
	neighbours: number[];
	/** Which cells are face up. */
	revealed: number[];
	/** Mines that have gone off, and who set them off. */
	exploded: { cell: number; seat: PlayerIndex }[];
	/** Safe reveals taken so far this turn — a flood-open counts as one. */
	streak: number;
	lastNote: string | null;
}

export interface MinesweeperMove {
	cell: number;
}

function around(cell: number): number[] {
	const col = cell % MINE_SIZE;
	const row = Math.floor(cell / MINE_SIZE);
	const cells: number[] = [];
	for (let dc = -1; dc <= 1; dc++) {
		for (let dr = -1; dr <= 1; dr++) {
			if (dc === 0 && dr === 0) continue;
			const c = col + dc;
			const r = row + dr;
			if (c < 0 || c >= MINE_SIZE || r < 0 || r >= MINE_SIZE) continue;
			cells.push(r * MINE_SIZE + c);
		}
	}
	return cells;
}

/** Opening one empty cell opens the whole empty patch, as it should. */
function floodFrom(start: number, mines: Set<number>, neighbours: number[], revealed: Set<number>) {
	const queue = [start];
	const opened: number[] = [];
	while (queue.length > 0) {
		const cell = queue.pop();
		if (cell === undefined || revealed.has(cell) || mines.has(cell)) continue;
		revealed.add(cell);
		opened.push(cell);
		if (neighbours[cell] === 0) queue.push(...around(cell));
	}
	return opened;
}

export const minesweeperDuel: GameDefinition<MinesweeperState, MinesweeperMove> = {
	id: "minesweeper",
	category: "puzzle",
	title: "Minesweeper Duel",
	emoji: "💣",
	blurb: "Clear squares for points — but ten mines are hiding in there.",
	howTo: [
		"Tap a square to clear it and score a point.",
		"A number tells you how many mines are touching that square.",
		"Clear a safe square and you go again — up to three in a row.",
		"Hit a mine and you lose three points and your turn.",
	],

	create(rng: Rng) {
		const cells = MINE_SIZE * MINE_SIZE;
		const mines = rng.shuffle([...Array(cells).keys()]).slice(0, MINE_COUNT);
		const mineSet = new Set(mines);
		const neighbours = Array.from(
			{ length: cells },
			(_, cell) => around(cell).filter((next) => mineSet.has(next)).length,
		);

		return {
			kind: "minesweeper",
			mines,
			neighbours,
			revealed: [],
			exploded: [],
			streak: 0,
			lastNote: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const cell = (raw as { cell?: unknown }).cell;
		if (typeof cell !== "number" || !Number.isInteger(cell)) return null;
		if (cell < 0 || cell >= MINE_SIZE * MINE_SIZE) return null;
		return { cell };
	},

	apply(state, player, move): MoveOutcome<MinesweeperState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.revealed.includes(move.cell)) return reject("That square is already open.");
		if (state.exploded.some((entry) => entry.cell === move.cell)) {
			return reject("That one has already gone off!");
		}

		const mines = new Set(state.mines);
		const scores: [number, number] = [...state.scores];

		if (mines.has(move.cell)) {
			scores[player] = Math.max(0, scores[player] - MINE_PENALTY);
			const exploded = [...state.exploded, { cell: move.cell, seat: player }];
			const finished = exploded.length === MINE_COUNT;
			return {
				ok: true,
				state: {
					...state,
					scores,
					exploded,
					streak: 0,
					lastNote: "💥 A mine! Three points gone.",
					turn: finished ? null : otherPlayer(player),
					result: finished ? resultFromScores(scores) : null,
				},
			};
		}

		const revealed = new Set(state.revealed);
		const opened = floodFrom(move.cell, mines, state.neighbours, revealed);
		scores[player] += opened.length;

		const safeCells = MINE_SIZE * MINE_SIZE - MINE_COUNT;
		const finished = revealed.size >= safeCells;
		// Clearing safely earns another go, but only so many: after a third
		// reveal in a row the board goes to the other player regardless.
		const streak = state.streak + 1;
		const keepTurn = streak < MINE_STREAK_LIMIT;

		return {
			ok: true,
			state: {
				...state,
				revealed: [...revealed],
				scores,
				streak: keepTurn ? streak : 0,
				lastNote:
					opened.length > 1
						? `Cleared ${opened.length} squares!`
						: keepTurn
							? "Safe — go again."
							: "Safe — three in a row, their turn now.",
				turn: finished ? null : keepTurn ? player : otherPlayer(player),
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const open = new Set(state.revealed);
		const gone = new Set(state.exploded.map((entry) => entry.cell));
		const moves: MinesweeperMove[] = [];
		for (let cell = 0; cell < MINE_SIZE * MINE_SIZE; cell++) {
			if (!open.has(cell) && !gone.has(cell)) moves.push({ cell });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state) {
		if (state.result) return state;
		const open = new Set(state.revealed);
		// The neighbour counts give the mines away just as surely as the mine
		// list does, so an unopened square sends `-1` rather than its number.
		return {
			...state,
			mines: state.exploded.map((entry) => entry.cell),
			neighbours: state.neighbours.map((count, cell) => (open.has(cell) ? count : -1)),
		};
	},
};
